import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import type { GetCreativeDataAsset2Query, GetCreativeDataAsset2QueryVariables } from '../../../../apis/graphql';
import type { Asset2Extended, Asset2GQL, Nullable } from '../../../../models';
import { transformAsset2GQLToAsset2 } from '../../../../utils/assets';
import { getAdCountryListForReview } from '../../../../utils/reviewDerivation';
import type { AdFormValues } from '../../adFormik';
import { getAssetIds } from '../../utils';
import type { CreativesBuilderValues } from '../types';
import { GET_CREATIVE_DATA_ASSET2 } from './queries/getCreativeDataAssets2';

/** Returns the percentage (0 through 1) of successful ingestions based on the total available */
function getSuccessPercent(assetsCount: number = 0, successCount: number = 0, withoutAssetsCount: number = 0): number {
  // Prevent a divide by 0 error; if there are no assets or trials without assets, assume 100% success rate
  if (!assetsCount && !withoutAssetsCount) return 1;

  return successCount / (assetsCount + withoutAssetsCount);
}

export type CreativeData = {
  assets: Asset2Extended[];
  successPercent: number;
};

export function useCreativeData(
  creative?: CreativesBuilderValues | null,
  publisherList?: Nullable<string[]>
): { data: CreativeData; loading: boolean; error?: ApolloError } {
  const { values } = useFormikContext<AdFormValues>();
  const countryList = useMemo(
    () => getAdCountryListForReview(values.lineItem?.countries, values.targetingTermValues ?? []),
    [values.lineItem?.countries, values.targetingTermValues]
  );
  const assetIdList = creative ? getAssetIds(creative) : [];

  const skip = !creative?.id || !assetIdList?.length;

  const { data: asset2Data, loading: asset2Loading, error: asset2Error } = useQuery<
    GetCreativeDataAsset2Query,
    GetCreativeDataAsset2QueryVariables
  >(GET_CREATIVE_DATA_ASSET2, {
    variables: { assetIdList },
    skip,
  });

  const rawAssets = (asset2Data?.getAsset2ByIDs as Asset2GQL[]) ?? [];
  const assets = rawAssets.map((asset) => transformAsset2GQLToAsset2(asset, publisherList, countryList));

  return {
    data: {
      assets,
      successPercent: getSuccessPercent(assets.length, assets.length, 0),
    },
    loading: asset2Loading,
    error: asset2Error,
  };
}
